import { useState } from "react";
import { GlassCard, Button, StatusPill, SecretReveal, useToast } from "@/shared/ui";
import { useKeys, useCreateKey, useRevokeKey } from "@/shared/api/hooks";
import { useI18n } from "@/shared/i18n";
import { ApiError } from "@/shared/api/client";
import type { KeyInfo } from "@/shared/api/schemas";

function fmt(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

export function KeysCard() {
  const toast = useToast();
  const { t } = useI18n();
  const keys = useKeys();
  const createKey = useCreateKey();
  const revokeKey = useRevokeKey();
  const [label, setLabel] = useState("");
  const [fresh, setFresh] = useState<string | null>(null);

  async function create() {
    try {
      const res = await createKey.mutateAsync(label);
      setFresh(res.api_key);
      setLabel("");
    } catch (e) {
      toast.error(e instanceof ApiError ? t(e.message) : t("Create failed"));
    }
  }

  async function revoke(k: KeyInfo) {
    if (
      !window.confirm(
        t("Revoke key \"{label}\"? This cannot be undone.", { label: k.label || k.id }),
      )
    )
      return;
    try {
      await revokeKey.mutateAsync(k.id);
      toast.success(t("Key revoked"));
    } catch (e) {
      toast.error(e instanceof ApiError ? t(e.message) : t("Revoke failed"));
    }
  }

  const list = keys.data ?? [];

  return (
    <GlassCard title={t("My API Keys")} hint={t("For programmatic calls (X-API-Key)")}>
      <div className="submit-row">
        <input
          className="input"
          placeholder={t("Label (optional, e.g. prod / test)")}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <Button onClick={create} loading={createKey.isPending}>
          {t("New key")}
        </Button>
      </div>

      {fresh && (
        <SecretReveal
          value={fresh}
          note={t(
            "You won't see this key again after leaving this page; if lost, revoke it and create a new one.",
          )}
        />
      )}

      <div className="acc-list" style={{ marginTop: "var(--s3)" }}>
        {keys.isLoading ? (
          <div className="acc-empty">{t("Loading…")}</div>
        ) : keys.isError ? (
          <div className="acc-empty">{t("Load failed — please refresh.")}</div>
        ) : !list.length ? (
          <div className="acc-empty">{t("No keys yet — click \"New key\" to create one.")}</div>
        ) : (
          list.map((k) => {
            const revoked = !!k.revoked_at;
            return (
              <div className="acc-row" key={k.id} style={revoked ? { opacity: 0.55 } : undefined}>
                <div className="acc-row__main">
                  <span className="acc-row__title">{k.label || t("Unnamed")}</span>
                  <span className="acc-row__meta">
                    {k.id.slice(0, 8)} · {t("created {ts}", { ts: fmt(k.created_at) })}
                  </span>
                </div>
                <div className="acc-row__right">
                  {revoked ? (
                    <StatusPill tone="error">{t("Revoked")}</StatusPill>
                  ) : (
                    <button
                      className="copy-btn"
                      onClick={() => revoke(k)}
                      disabled={revokeKey.isPending}
                    >
                      {t("Revoke")}
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </GlassCard>
  );
}
